import React, { useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import type { PartsToOrderPayload } from '@techgitter/contracts';
import { useSignalREvent } from '../../hooks/useSignalR';
import { useCurrentUser } from '../../contexts/CurrentUserContext';

interface Props {
  onView: () => void;
}

/**
 * Toast handler for `parts:to_order` SignalR events.
 *
 * The backend currently broadcasts hub-wide rather than per-user, so we filter
 * here on the Purchasing role to avoid notifying technicians about basket
 * additions they can't act on. Admins without Purchasing also see nothing —
 * the role is the function ("you order parts"), not the position.
 */
export const PartsToOrderNotifier: React.FC<Props> = ({ onView }) => {
  const currentUser = useCurrentUser();
  const canReceive = !!currentUser?.hasRole('Purchasing');
  const [payload, setPayload] = useState<PartsToOrderPayload | null>(null);

  useSignalREvent('parts:to_order', (data: unknown) => {
    if (!canReceive) return;
    if (data && typeof data === 'object' && 'partOrderLineId' in data) {
      setPayload(data as PartsToOrderPayload);
    }
  });

  if (!canReceive) return null;

  return (
    <Snackbar
      open={!!payload}
      autoHideDuration={8000}
      onClose={() => setPayload(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        severity="info"
        onClose={() => setPayload(null)}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setPayload(null);
              onView();
            }}
          >
            View
          </Button>
        }
      >
        {payload && (
          <>
            Part needed: <strong>{payload.description}</strong> ({payload.qty}) — {payload.vendorName}
          </>
        )}
      </Alert>
    </Snackbar>
  );
};
